import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Container } from 'react-bootstrap';
import { createSimpleNewsTickerInstance } from '../../Models/SimpleNewsTicker';
import { generateDynamicCSS } from '../../ComponentsMap';
import { useTranslation } from 'react-i18next';
import { Spinner } from 'react-bootstrap';
import './SimpleNewsTicker.css';

const SimpleNewsTicker = () => {
  const { i18n } = useTranslation();
  const [ticker, setTicker] = useState(createSimpleNewsTickerInstance());
  const [isPaused, setIsPaused] = useState(false);
  const tickerRef = useRef(null);
  const [position, setPosition] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [direction, setDirection] = useState(localStorage.getItem('selectedDirection') || 'ltr');

  // Get current language from i18n
  const currentLanguage = i18n.language;
  
  // Memoized function to load ticker data
  const loadTickerData = useCallback(async (language) => {
    try {
      setIsLoading(true);
      await ticker.load(language);
      
      const loadedStyles = ticker.getStyle();
      if (loadedStyles) {
        Object.entries(loadedStyles).forEach(([className, styles]) => {
          generateDynamicCSS(className, [styles]);
        });
      }
      
      setIsLoading(false);
    } catch (err) {
      console.error('Failed to load news ticker data:', err);
      setError('Failed to load news ticker data');
      setIsLoading(false);
    }
  }, [ticker]);

  // Handle language change from i18n
  const handleLanguageChange = useCallback((newLanguage) => {
    loadTickerData(newLanguage);
  }, [loadTickerData]);

  useEffect(() => {
    loadTickerData(currentLanguage);
  }, [loadTickerData, currentLanguage]);

  // Listen for language changes
  useEffect(() => {
    const handleLanguageChangeEvent = () => {
      loadTickerData(i18n.language);
    };

    i18n.on('languageChanged', handleLanguageChangeEvent);
    
    return () => {
      i18n.off('languageChanged', handleLanguageChangeEvent);
    };
  }, [i18n, loadTickerData]);

  useEffect(() => {
    const storedDirection = localStorage.getItem('selectedDirection');
    if (storedDirection === 'rtl' || storedDirection === 'ltr') {
      setDirection(storedDirection);
    }

    const handleStorageDirectionChange = (event) => {
      if (event.key === 'selectedDirection' && (event.newValue === 'rtl' || event.newValue === 'ltr')) {
        setDirection(event.newValue);
      }
    };

    window.addEventListener('storage', handleStorageDirectionChange);

    return () => {
      window.removeEventListener('storage', handleStorageDirectionChange);
    };
  }, []);

  useEffect(() => {
    let animationFrame;
    let lastTime = 0;
    const tickerElement = tickerRef.current;

    const animate = (currentTime) => {
      if (!lastTime) lastTime = currentTime;
      const deltaTime = currentTime - lastTime;

      if (!isPaused && !isLoading && deltaTime > 16 && tickerElement) {
        setPosition((prevPosition) => {
          const newPosition = prevPosition + 1;
          return newPosition >= tickerElement.scrollWidth ? 0 : newPosition;
        });
        lastTime = currentTime;
      }

      animationFrame = requestAnimationFrame(animate);
    };

    if (!isLoading && tickerElement) {
      animationFrame = requestAnimationFrame(animate);
    }

    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [isPaused, isLoading]);

  if (isLoading) {
    return <div className="loading-placeholder">
      <Spinner animation="border" role="status">
      </Spinner>
    </div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  if (!ticker.getIsEnabled()) {
    return null;
  }

  return (
    <Container fluid className="news-ticker-container p-0 newsTickerContainer" dir={direction}>
      
      <div 
        className="news-ticker"
        ref={tickerRef}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        <div 
          className="news-ticker-content"
          style={{ transform: `translateX(${direction === 'rtl' ? position : -position}px)` }}
        >
          <div className="news-item NewsTickerTitle">
            {ticker.getTitle()}
          </div>
          {ticker.getNewsItems().map((item, index) => (
            <div key={index} className="news-item newsItem">
              {item}
            </div>
          ))}
          {/* Duplicate items for seamless loop */}
          <div className="news-item NewsTickerTitle">
            {ticker.getTitle()}
          </div>
          {ticker.getNewsItems().map((item, index) => (
            <div key={`duplicate-${index}`} className="news-item newsItem">
              {item}
            </div>
          ))}
        </div>
      </div>
    </Container>
  );
};

export default SimpleNewsTicker;
